import { OrganizationService } from "@buf/brevdev_devplane.connectrpc_es/devplaneapi/v1/organization_connect";
import { ProgramService } from "@buf/brevdev_devplane.connectrpc_es/devplaneapi/v1/program_connect";
import { ReservedInstancePoolService } from "@buf/brevdev_devplane.connectrpc_es/devplaneapi/v1/reserved_instance_pool_connect";
import type { Client } from "@connectrpc/connect";
import { createConnectClient } from "../connect";

type BaseRes = {
  success?: boolean;
  message?: string;
};

type DevplaneAdminClients = {
  program: Client<typeof ProgramService>;
  reservedInstancePool: Client<typeof ReservedInstancePoolService>;
  organization: Client<typeof OrganizationService>;
};

const clients: DevplaneAdminClients = {
  program: createConnectClient(ProgramService),
  reservedInstancePool: createConnectClient(ReservedInstancePoolService),
  organization: createConnectClient(OrganizationService),
};

type DevplaneAdminClientOverrides = {
  [K in keyof DevplaneAdminClients]?: Partial<DevplaneAdminClients[K]>;
};

const ok = <T>(data: T): BaseRes & { data: T } => ({
  success: true,
  data,
});

export function createDevplaneAdmin(
  overrides: DevplaneAdminClientOverrides = {},
) {
  const c = {
    program: { ...clients.program, ...overrides.program },
    reservedInstancePool: {
      ...clients.reservedInstancePool,
      ...overrides.reservedInstancePool,
    },
    organization: { ...clients.organization, ...overrides.organization },
  };

  return {
    listPrograms: async () => ok((await c.program.listProgram({})).items),
    createProgram: async (displayName: string) =>
      ok((await c.program.createProgram({ displayName })).program),
    listReservedInstancePools: async () =>
      ok((await c.reservedInstancePool.queryReservedInstancePools({})).items),
    updateOrgReservedInstancePools: async (
      orgId: string,
      reservedInstancePoolIds: string[],
    ) =>
      ok(
        (
          await c.organization.updateOrganization({
            organizationId: orgId,
            updateReservedInstancePoolIdsRequest: {
              reservedInstancePoolIds,
            },
          })
        ).organization,
      ),
    createReservedInstancePool: async (displayName: string) =>
      ok(
        (
          await c.reservedInstancePool.createReservedInstancePool({
            displayName,
          })
        ).reservedInstancePool,
      ),
    updateReservedInstancePool: async (poolId: string, displayName: string) =>
      ok(
        (
          await c.reservedInstancePool.setReservedInstancePoolDisplayName({
            id: poolId,
            displayName,
          })
        ).reservedInstancePool,
      ),
    deleteReservedInstancePool: async (poolId: string) => {
      await c.reservedInstancePool.deleteReservedInstancePool({ id: poolId });
      return ok(undefined);
    },
  };
}

export const DevplaneAdmin = createDevplaneAdmin();
